import { Head, usePage } from "@inertiajs/react"
import type { ReactNode } from "react"
import MatchLayout from "@/layouts/match-layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Upload } from "lucide-react"

interface PlayerData {
  id: number
  name: string
  first_name: string | null
  last_name: string | null
  dni: string | null
  email: string | null
  category: string | null
  sex: string | null
  position: string | null
  team_name: string
  has_user: boolean
}

export default function StaffPlayersIndex() {
  const { players } = usePage().props as unknown as { players: PlayerData[] }

  return (
    <>
      <Head title="Players" />
      <div className="border-b bg-card">
        <div className="mx-auto max-w-5xl px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Players</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {players.length} player{players.length !== 1 ? "s" : ""} registered
            </p>
          </div>
          <ImportButton />
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-6">
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-xs font-medium uppercase text-muted-foreground">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">DNI</th>
                    <th className="px-4 py-3">Team</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Position</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">User</th>
                  </tr>
                </thead>
                <tbody>
                  {players.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                        No players found. Import a CSV to get started.
                      </td>
                    </tr>
                  ) : (
                    players.map(player => (
                      <tr key={player.id} className="border-b last:border-0">
                        <td className="px-4 py-3 text-sm font-medium">{player.name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{player.dni || "—"}</td>
                        <td className="px-4 py-3 text-sm">{player.team_name}</td>
                        <td className="px-4 py-3 text-sm">{player.category || "—"}</td>
                        <td className="px-4 py-3 text-sm">{player.position || "—"}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{player.email || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            player.has_user
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                          }`}>
                            {player.has_user ? "Yes" : "No"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

StaffPlayersIndex.layout = (page: ReactNode) => <MatchLayout>{page}</MatchLayout>

function ImportButton() {
  return (
    <form action="/staff/players/import" method="POST" encType="multipart/form-data">
      <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""} />
      <label htmlFor="player-csv-upload">
        <Button type="button" variant="outline" onClick={() => document.getElementById("player-csv-upload")?.click()}>
          <Upload className="mr-2 h-4 w-4" />
          Import Players CSV
        </Button>
      </label>
      <input
        id="player-csv-upload"
        type="file"
        accept=".csv"
        name="csv_file"
        className="hidden"
        onChange={(e) => {
          const form = e.target.closest("form") as HTMLFormElement
          if (e.target.files?.[0] && form) form.submit()
        }}
      />
    </form>
  )
}
